import { Event, useStore } from '@mirai/data-sources';
import { useLocale } from '@mirai/locale';
import { ServiceRates } from '@mirai/services';
import { useDevice } from '@mirai/ui';
import { useEffect, useRef, useState } from 'react';

import { debounce, fetchUrlParams } from '../../../helpers';
import { getSafeTrust } from '../../../trust';
import { EVENT, METRICS_EVENTS, parseToParties, replaceUrlParams } from '../../helpers';
import { getBoard, getFinderParams, getNetPrice, signout } from '../helpers';
import { NOTIFICATION, UTM_SOURCE_NAME } from '../Rates.constants';
import { L10N } from '../Rates.l10n';

let fetchCall = 0;
const WARNING = 'warning';

export const useRates = () => {
  const { isMobile, mobile } = useDevice();
  const { translate } = useLocale();
  const { set, value: store } = useStore();
  const {
    club = {},
    currency,
    finder = {},
    hotel = {},
    id,
    language,
    locale,
    rates: { cart: storeCart = [], meta } = {},
    occupation: { maxRooms } = {},
    urlParams,
    session,
    skeleton = false,
  } = store;
  const refRoomSelector = useRef();

  const [cart, setCart] = useState([]);
  const [checkoutDataSource, setCheckoutDataSource] = useState();
  const [clubDiscount, setClubDiscount] = useState(club.discount);
  const [dataSource, setDataSource] = useState();
  const [fetching, setFetching] = useState(false);
  const [metaBoard, setMetaBoard] = useState();
  const [responseError, setResponseError] = useState();
  const [room, setRoom] = useState();
  const [strictSearch, setStrictSearch] = useState();

  const { calendar = [], occupation = [], place, promocode } = finder;
  const device = mobile ? 'MOBILE' : 'DESKTOP_TABLET';
  const multiRoom = occupation?.length > 1;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    skeleton && setFetching(true);
  }, [skeleton]);

  useEffect(() => {
    if (multiRoom) {
      if (room > 0) {
        const { current: { offsetHeight = 0, offsetTop = 0 } = {} } = refRoomSelector || {};
        window.scrollTo({ top: offsetTop + (isMobile ? offsetHeight : 0), behavior: 'smooth' });
      }
      setDataSource();
    }

    (room !== undefined || multiRoom) && debounceFetch({ nextRoom: room });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room]);

  useEffect(() => {
    const callback = async (next) => {
      setMetaBoard();
      setClubDiscount(typeof next !== 'object' ? next : undefined);
    };
    Event.subscribe(EVENT.RATES_FILTER_CLUB, callback);

    return () => Event.unsubscribe(EVENT.RATES_FILTER_CLUB, callback);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if ((!dataSource && (!responseError || !responseError.tracking)) || room) return;

    if (dataSource) getSafeTrust(store).record('ratesObserved', { ...store, dataSource });
    Event.publish(EVENT.RATES_RESPONSE, {
      error: !!responseError,
      event: EVENT.RATES_RESPONSE,
      response: {
        ...(dataSource || responseError),
        currency,
        finder: { calendar, occupation, place, promocode },
        language,
        session,
      },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSource, responseError]);

  useEffect(() => {
    if (!dataSource || !storeCart.length) return;

    getBoard({ cart, dataSource, storeCart })
      .then((board) => {
        if (!meta && multiRoom) {
          const nextCart = [...cart, { ...board, netPrice: getNetPrice(board) }];
          setCart(nextCart);
          if (nextCart.length < storeCart.length && nextCart.length < occupation.length)
            return setRoom(nextCart.length);
          return set({ rates: undefined });
        }

        if (meta) {
          Event.publish(EVENT.NOTIFICATION, {
            contrast: true,
            defaultMessage: translate(L10N.NOTIFICATION_META_RATE_FOUND, {
              utm_source: UTM_SOURCE_NAME[urlParams.utm_source] || urlParams.utm_source,
            }),
          });
        }

        setMetaBoard(board);
        set({ rates: undefined });
      })
      .catch(() => {
        set({ rates: undefined });
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSource]);

  useEffect(() => {
    if (!clubDiscount && !currency && !session && strictSearch === undefined) return;

    setCart([]);
    setRoom(undefined);
    debounceFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubDiscount, currency, session, strictSearch]);

  useEffect(() => {
    if (!locale) return;

    debounceFetch({ nextRoom: room });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  const checkNotifications = (nextDataSource, nextCalendar, nextOccupation, nextPlace, nextPromocode) => {
    const { notifications = [] } = nextDataSource;
    const trackNotification = (notificationType, notificationMessage) => {
      Event.publish(EVENT.METRICS, {
        id: METRICS_EVENTS.RATES_INTERACTION,
        action: 'notification_show',
        notification_type: notificationType,
        notification_message: notificationMessage,
      });
    };

    if (notifications.includes(NOTIFICATION.PROMOCODE_NOT_FOUND) && nextPromocode) {
      Event.publish(EVENT.NOTIFICATION, {
        contrast: true,
        defaultMessage: translate(L10N.NOTIFICATION_PROMOCODE_NOT_FOUND, { promocode: nextPromocode }),
      });

      trackNotification(WARNING, 'promo_code_unavailable');
      set({ finder: { calendar: nextCalendar, occupation: nextOccupation, place: nextPlace, promocode: '' } });
      Event.publish(EVENT.RATES_FILTER_PROMOCODE, { promocode: undefined });
    } else if (nextPromocode !== finder.promocode) {
      Event.publish(EVENT.RATES_FILTER_PROMOCODE, { promocode: nextPromocode });
    }
    if (notifications.includes(NOTIFICATION.ROOM_NOT_FOUNT) && urlParams.roomTypeId) {
      Event.publish(EVENT.NOTIFICATION, {
        contrast: true,
        defaultMessage: translate(L10N.NOTIFICATION_ROOM_NOT_FOUND),
      });
      trackNotification(WARNING, 'room_unavailable');
    }
    if (notifications.includes(NOTIFICATION.OFFER_NOT_FOUND) && urlParams.idoffers) {
      Event.publish(EVENT.NOTIFICATION, {
        contrast: true,
        defaultMessage: translate(L10N.NOTIFICATION_OFFER_NOT_FOUND),
      });
      trackNotification(WARNING, 'offer_unavailable');
    }
    const { notification } = fetchUrlParams(location.search) || {};
    const validateError = notification && L10N.NOTIFICATION_VALIDATE[notification];
    if (validateError) {
      replaceUrlParams({ notification: undefined });
      Event.publish(EVENT.NOTIFICATION, { warning: true, defaultMessage: translate(validateError) });
      trackNotification(WARNING, 'rate_validation_error');
    }
  };

  const fetch = async ({
    nextCalendar = calendar,
    nextRoom,
    nextOccupation = occupation,
    nextPlace = place,
    nextPromocode = finder.promocode,
  } = {}) => {
    if (skeleton) return;

    const { id: [currentId] = [] } = place || {};
    const { id: [nextId] = [] } = nextPlace || {};
    let nextClubDiscount;
    if (nextPlace?.isHotel && currentId !== nextId) {
      const { variants = [] } = club;
      const currentVariant = variants.find(({ id = '' } = {}) => id.toString() === nextId.toString());
      nextClubDiscount = currentVariant?.discount;
      if (currentVariant && nextClubDiscount !== clubDiscount) {
        Event.publish(EVENT.RATES_FILTER_CLUB, nextClubDiscount);

        return;
      }
    }

    Event.publish(EVENT.FINDER, { calendar: undefined });

    setFetching(true);
    setResponseError();

    let urlParams = fetchUrlParams(location.search);
    if (!urlParams.checkin || !urlParams.nights || !urlParams.parties) {
      urlParams = { ...urlParams, ...getFinderParams({ ...finder, hotel }) };
      replaceUrlParams(urlParams);
    }
    if (nextPlace && !nextPlace.isHotel) {
      replaceUrlParams({ ...urlParams, idtokenprovider: undefined });
    }

    const params = {
      currency,
      ...urlParams,
      ...(club.active ? { club, clubDiscount } : undefined),
      id,
      locale,
      maxRooms,
      device,
      occupation: nextOccupation,
      place,
      selectedIds: cart.map(({ roomId } = {}) => roomId).join(','),
      session,
      strictSearch,
    };
    if (nextRoom !== undefined) params.parties = parseToParties([nextOccupation[nextRoom || 0]]);

    const lastFetchCall = ++fetchCall;
    const nextDataSource = await ServiceRates.get(params).catch(setResponseError);
    if (lastFetchCall !== fetchCall) return;

    if (nextDataSource?.externalSignout) return signout({ club, language });

    if (nextDataSource && nextRoom === undefined) {
      checkNotifications(nextDataSource, nextCalendar, nextOccupation, nextPlace, nextPromocode);

      if (nextOccupation?.length > 1 && nextRoom === undefined) setRoom(0);
    }

    if (nextDataSource) setCheckoutDataSource(nextDataSource);

    setDataSource(nextDataSource);
    setFetching(false);
  };

  const debounceFetch = debounce(fetch, 60);

  return {
    actions: { setCart, setDataSource, setMetaBoard, setRoom, setStrictSearch },
    device,
    multiRoom,
    fetch: debounceFetch,
    refRoomSelector,
    responseError,
    state: { cart, checkoutDataSource, clubDiscount, dataSource, fetching, metaBoard, room, strictSearch },
  };
};
